import { grossToNet, isItemTaxable, roundMoney, roundPrice } from './priceIgvUtils';
import { getItemLineSubtotal } from './unitSelectUtils';

/** Tasa general de IGV para el módulo Ventas (Perú). */
export const VENTAS_IGV_RATE = 18;

export const VENTAS_IGV_OPTIONS = [
  { value: '18', label: '18%' },
  { value: '10', label: '10%' },
  { value: '10.5', label: '10.5%' },
];

export const parseIgvRate = (value, fallback = VENTAS_IGV_RATE) => {
  const rate = parseFloat(value);
  return Number.isFinite(rate) && rate > 0 ? rate : fallback;
};

export const calcVentasLine = (item, igvRate = VENTAS_IGV_RATE, options = {}) => {
  const {
    product = null,
    productConversionsEnabled = false,
    useConvertedQuantityForSubtotal = true,
  } = options;

  let subtotal;
  if (productConversionsEnabled && product) {
    subtotal = roundMoney(
      getItemLineSubtotal({
        product,
        unitCode: item.unit_of_measure,
        quantity: item.quantity,
        unitPrice: item.unit_price,
        productConversionsEnabled,
        useConvertedQuantityForSubtotal,
      }),
    );
  } else {
    const qty = roundPrice(parseFloat(item.quantity) || 0, 3);
    const unitPrice = roundPrice(parseFloat(item.unit_price) || 0, 4);
    subtotal = roundMoney(qty * unitPrice);
  }
  const hasIgv =
    item.has_igv === undefined || item.has_igv === null
      ? true
      : isItemTaxable(item.has_igv);
  const rate = hasIgv ? igvRate : 0;
  const igvAmount = hasIgv ? roundMoney(subtotal * (igvRate / 100)) : 0;
  const total = roundMoney(subtotal + igvAmount);
  return { subtotal, igvAmount, total, hasIgv, rate };
};

export const resolveDiscountOfferPercent = (item) => {
  const stored = parseFloat(item?.discount_percent);
  if (Number.isFinite(stored) && stored > 0 && stored <= 100) {
    return stored;
  }
  const listGross = parseFloat(item?.list_unit_price_gross);
  const offerGross = parseFloat(item?.offer_unit_price_gross);
  if (!Number.isFinite(listGross) || listGross <= 0 || !Number.isFinite(offerGross) || offerGross >= listGross) {
    return 0;
  }
  return roundPrice((1 - offerGross / listGross) * 100, 4);
};

export const resolveOfferBaseNetLine = (item, line, igvRate, qty) => {
  const hasIgv = item?.has_igv !== false;
  if (item?.offer_kind === 'multi_product') {
    return line.subtotal;
  }
  const listGross = parseFloat(item?.list_unit_price_gross);
  if (Number.isFinite(listGross) && listGross > 0) {
    const listUnit = ventasPosPriceToUnitNet(listGross, { hasIgv, igvRate });
    if (listUnit !== '') {
      return roundMoney((typeof listUnit === 'number' ? listUnit : parseFloat(listUnit)) * qty);
    }
  }
  return line.subtotal;
};

export const hasOfferDiscountInTotals = (item) => {
  const discountPercent = resolveDiscountOfferPercent(item);
  if (discountPercent <= 0) return false;
  if (item?.offer_kind === 'multi_product') {
    return Boolean(item?.sales_offer_id);
  }
  if (item?.sales_offer_id) return true;
  return hasDiscountOfferLine(item);
};

export const repriceItemFromOfferMetadata = (item, igvRate) => {
  if (!item?.sales_offer_id) return null;
  const hasIgv = item?.has_igv !== false;
  let offerGross = null;
  if (!hasIgv) {
    offerGross = parseFloat(item.list_unit_price_gross);
  } else if (item.offer_kind === 'multi_product') {
    offerGross = parseFloat(item.bundle_unit_price_gross ?? item.offer_unit_price_gross);
  } else {
    offerGross = parseFloat(item.offer_unit_price_gross);
  }
  if (!Number.isFinite(offerGross)) return null;
  const net = ventasPosPriceToUnitNet(offerGross, { hasIgv, igvRate });
  if (net === '') return null;
  return copyOfferLineMetadata(item, { ...item, unit_price: net });
};

export const calcVentasHeader = (items, igvRate = VENTAS_IGV_RATE, options = {}) => {
  // Descuento gravado: % sobre base imponible neta (sin IGV). IGV se calcula después sobre base − descuento.
  // Descuento no gravado: % sobre monto no gravado (sin IGV).
  let baseList = 0;
  let taxableNet = 0;
  let discountTaxableNet = 0;
  let discountNoGravadosNet = 0;
  let noGravados = 0;
  let noGravadosBase = 0;

  (items || []).forEach((item) => {
    const isService = item.item_type === 'service';
    const product = isService
      ? null
      : (options.getProduct?.(item.product_id, item.product) ?? item.product ?? null);
    const line = calcVentasLine(item, igvRate, { ...options, product });
    const qty = roundPrice(parseFloat(item.quantity) || 0, 3);

    if (!line.hasIgv) {
      if (hasOfferDiscountInTotals(item)) {
        const discountPercent = resolveDiscountOfferPercent(item);
        const baseLine = roundMoney(resolveOfferBaseNetLine(item, line, igvRate, qty));
        const discountNetLine = roundMoney(baseLine * (discountPercent / 100));
        const netLine = roundMoney(baseLine - discountNetLine);

        noGravadosBase += baseLine;
        discountNoGravadosNet += discountNetLine;
        noGravados += netLine;
      } else {
        noGravados += line.subtotal;
        noGravadosBase += line.subtotal;
      }
      return;
    }

    if (hasOfferDiscountInTotals(item)) {
      const discountPercent = resolveDiscountOfferPercent(item);
      const baseNetLine = roundMoney(resolveOfferBaseNetLine(item, line, igvRate, qty));
      const discountNetLine = roundMoney(baseNetLine * (discountPercent / 100));
      const taxableNetLine = roundMoney(baseNetLine - discountNetLine);

      baseList += baseNetLine;
      discountTaxableNet += discountNetLine;
      taxableNet += taxableNetLine;
      return;
    }

    baseList += line.subtotal;
    taxableNet += line.subtotal;
  });

  const igvAmount = roundMoney(taxableNet * (igvRate / 100));
  const descuento = roundMoney(discountTaxableNet + discountNoGravadosNet);

  return {
    base_imponible: roundMoney(baseList),
    base_imponible_neta: roundMoney(taxableNet),
    descuento,
    descuento_gravado: roundMoney(discountTaxableNet),
    descuento_no_gravado: roundMoney(discountNoGravadosNet),
    igv_amount: igvAmount,
    no_gravados: roundMoney(noGravados),
    no_gravados_bruto: roundMoney(noGravadosBase),
    total_amount: roundMoney(taxableNet + igvAmount + noGravados),
  };
};

export const formatIgvRateLabel = (rate = VENTAS_IGV_RATE) => `${rate}%`;

/** Precio bruto (con IGV) del catálogo POS de Ventas. */
export const resolvePosListGrossPrice = (product, saleTypeId = null) => {
  if (!product) return null;
  if (saleTypeId != null && saleTypeId !== '' && product.type_prices) {
    const typePrice = product.type_prices[String(saleTypeId)] ?? product.type_prices[saleTypeId];
    if (typePrice != null && typePrice !== '') {
      const typed = parseFloat(typePrice);
      if (Number.isFinite(typed) && typed >= 0) return typed;
    }
  }
  const raw = product.list_price ?? product.price;
  if (raw === null || raw === undefined || raw === '') return null;
  const value = parseFloat(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

/** Precio bruto (con IGV) del catálogo POS de Ventas. */
export const getProductPosGrossPrice = (product, saleTypeId = null) => {
  if (!product) return null;
  if (saleTypeId != null && saleTypeId !== '' && product.type_prices) {
    const typePrice = product.type_prices[String(saleTypeId)] ?? product.type_prices[saleTypeId];
    if (typePrice != null && typePrice !== '') {
      const typed = parseFloat(typePrice);
      if (Number.isFinite(typed) && typed >= 0) return typed;
    }
  }
  const raw = product.effective_price ?? product.list_price ?? product.price;
  if (raw === null || raw === undefined || raw === '') return null;
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : null;
};

/**
 * Precio de lista POS (siempre con IGV) → neto para unit_price del formulario.
 */
export const ventasPosPriceToUnitNet = (grossPrice, { hasIgv, igvRate }) => {
  const gross = parseFloat(grossPrice);
  if (!Number.isFinite(gross) || gross < 0) return '';
  if (gross === 0) return 0;
  if (isItemTaxable(hasIgv)) {
    return grossToNet(gross, igvRate);
  }
  return roundPrice(gross);
};

/** Precio guardado en lista POS de servicios → neto para líneas de venta. */
export const servicePosPriceToUnitNet = (storedPrice, priceIncludesIgv, igvRate = VENTAS_IGV_RATE) => {
  const raw = parseFloat(storedPrice);
  if (!Number.isFinite(raw) || raw < 0) return '';
  if (raw === 0) return 0;
  if (priceIncludesIgv) {
    return grossToNet(raw, igvRate);
  }
  return roundPrice(raw);
};

/** Fila de lista de precios del servicio en el POS activo. */
export const resolveServicePosListPrice = (service, pointOfSaleId) => {
  if (!service?.pos_prices || !pointOfSaleId) return null;
  const row = service.pos_prices.find(
    (p) => Number(p.point_of_sale_id) === Number(pointOfSaleId),
  );
  if (!row || row.price === null || row.price === undefined || row.price === '') {
    return null;
  }
  const storedPrice = parseFloat(row.price);
  if (!Number.isFinite(storedPrice) || storedPrice < 0) return null;
  return {
    storedPrice,
    priceIncludesIgv: row.price_includes_igv === true,
  };
};

/** Aplica precio de lista POS al ítem de servicio (unit_price siempre neto para el formulario). */
export const applyServiceListPriceToLine = (item, service, pointOfSaleId, igvRate) => {
  const list = resolveServicePosListPrice(service, pointOfSaleId);
  if (!list) return item;
  const rate = parseIgvRate(igvRate);
  const net = servicePosPriceToUnitNet(list.storedPrice, list.priceIncludesIgv, rate);
  if (net === '') return item;
  return {
    ...item,
    unit_price: net,
    service_list_price_includes_igv: list.priceIncludesIgv,
  };
};

export const clearDiscountOfferFields = (item) => {
  const next = { ...item };
  delete next.sales_offer_id;
  delete next.offer_kind;
  delete next.list_unit_price_gross;
  delete next.offer_unit_price_gross;
  delete next.catalog_selection_id;
  delete next.offer_name;
  delete next.offer_code;
  delete next.discount_percent;
  delete next.bundle_unit_price_gross;
  return next;
};

/** Unidad fija para ofertas combo (varios productos). */
export const MULTI_PRODUCT_OFFER_UNIT = 'UNI';

/** Suma de precios base (con IGV) de los productos del combo. */
export const computeMultiProductListGross = (offer) =>
  (offer?.items || []).reduce((sum, item) => {
    const base = parseFloat(item?.base_price);
    const qty = parseFloat(item?.quantity) || 1;
    if (!Number.isFinite(base)) return sum;
    return sum + base * qty;
  }, 0);

export const isMultiProductOfferLine = (item) => item?.offer_kind === 'multi_product';

export const getItemCatalogSelectionValue = (item) => {
  if (item?.catalog_selection_id) return String(item.catalog_selection_id);
  if (item?.sales_offer_id && (item?.offer_kind === 'multi_product' || hasDiscountOfferLine(item))) {
    return `offer-${item.sales_offer_id}`;
  }
  return item?.product_id ? String(item.product_id) : '';
};

export const copyOfferLineMetadata = (from, to) => {
  if (!from?.sales_offer_id) return to;
  return {
    ...to,
    sales_offer_id: from.sales_offer_id,
    offer_kind: from.offer_kind,
    list_unit_price_gross: from.list_unit_price_gross,
    offer_unit_price_gross: from.offer_unit_price_gross,
    bundle_unit_price_gross: from.bundle_unit_price_gross,
    catalog_selection_id: from.catalog_selection_id,
    offer_name: from.offer_name,
    offer_code: from.offer_code,
    discount_percent: from.discount_percent,
    unit_of_measure:
      from.offer_kind === 'multi_product' ? MULTI_PRODUCT_OFFER_UNIT : to.unit_of_measure,
  };
};

export const applyMultiProductOfferFields = (item, offer, bundleGross = null) => {
  const offerGrossValue = parseFloat(bundleGross ?? offer?.bundle_offer_price ?? offer?.effective_price);
  const listGross = computeMultiProductListGross(offer);
  const resolvedListGross =
    Number.isFinite(listGross) && listGross > 0 ? listGross : offerGrossValue;
  const firstItem = offer?.items?.[0];
  const discountPercent = offer?.discount_percent ?? offer?.discount_value ?? null;
  return {
    ...item,
    sales_offer_id: offer.offer_id,
    offer_kind: 'multi_product',
    catalog_selection_id: offer.id,
    offer_name: offer.name,
    offer_code: offer.sku,
    bundle_unit_price_gross: Number.isFinite(offerGrossValue) ? roundPrice(offerGrossValue) : null,
    list_unit_price_gross: Number.isFinite(resolvedListGross) ? roundPrice(resolvedListGross) : null,
    offer_unit_price_gross: Number.isFinite(offerGrossValue) ? roundPrice(offerGrossValue) : null,
    discount_percent:
      discountPercent != null && Number.isFinite(parseFloat(discountPercent))
        ? parseFloat(discountPercent)
        : null,
    product_id: firstItem ? String(firstItem.product_id) : item.product_id,
    unit_of_measure: MULTI_PRODUCT_OFFER_UNIT,
  };
};

export const buildDiscountOfferCatalogEntry = (item, product = null) => {
  if (!hasDiscountOfferLine(item) || !item?.sales_offer_id) return null;
  const productId = parseInt(item.product_id, 10);
  return {
    catalog_type: 'offer',
    id: item.catalog_selection_id || `offer-${item.sales_offer_id}`,
    offer_id: item.sales_offer_id,
    sku: item.offer_code || product?.offer_code || product?.sku,
    name: item.offer_name || product?.offer_name || item.offer_code || 'Oferta',
    offer_kind: 'discount',
    product_id: Number.isFinite(productId) ? productId : null,
    effective_price: item.offer_unit_price_gross,
    list_price: item.list_unit_price_gross,
    unit_of_measure: item.unit_of_measure || product?.unit_of_measure,
    items: [
      {
        product_id: productId,
        base_price: item.list_unit_price_gross,
        offer_price: item.offer_unit_price_gross,
        unit_of_measure: item.unit_of_measure || product?.unit_of_measure,
        sku: product?.sku,
        name: product?.name,
      },
    ],
  };
};

export const buildOfferCatalogEntry = (item, product = null) => {
  if (!item?.sales_offer_id) return null;
  if (item.offer_kind === 'discount') return buildDiscountOfferCatalogEntry(item, product);
  if (item.offer_kind !== 'multi_product' && item.bundle_unit_price_gross == null) return null;

  const productId = parseInt(item.product_id, 10);
  const bundleGross = item.bundle_unit_price_gross ?? item.offer_unit_price_gross;
  const listGross = item.list_unit_price_gross ?? bundleGross;
  return {
    catalog_type: 'offer',
    id: item.catalog_selection_id || `offer-${item.sales_offer_id}`,
    offer_id: item.sales_offer_id,
    sku: item.offer_code,
    name: item.offer_name || item.offer_code || 'Combo',
    offer_kind: 'multi_product',
    discount_percent: item.discount_percent ?? null,
    bundle_offer_price: bundleGross,
    effective_price: bundleGross,
    list_price: listGross,
    product_id: Number.isFinite(productId) ? productId : null,
    unit_of_measure: MULTI_PRODUCT_OFFER_UNIT,
    items: Number.isFinite(productId)
      ? [
          {
            product_id: productId,
            sku: product?.sku,
            name: product?.name,
            unit_of_measure: MULTI_PRODUCT_OFFER_UNIT,
          },
        ]
      : [],
  };
};

export const materializeLineFromOffer = (offer, item, { igvRate, lineDefaults = {} } = {}) => {
  if (!offer || offer.catalog_type !== 'offer') return item;

  const base = {
    ...item,
    item_type: 'product',
    service_id: '',
    project_id: lineDefaults.project_id ?? item.project_id,
    has_igv: lineDefaults.has_igv ?? item.has_igv ?? true,
  };

  if (offer.offer_kind === 'discount' && offer.product_id) {
    const offerItem = offer.items?.[0];
    const offerGross = parseFloat(offerItem?.offer_price ?? offer.effective_price);
    const net = ventasPosPriceToUnitNet(offerGross, {
      hasIgv: base.has_igv ?? true,
      igvRate,
    });
    let updated = {
      ...base,
      product_id: String(offer.product_id),
      unit_of_measure: offer.unit_of_measure || offerItem?.unit_of_measure || '',
      quantity: offerItem?.quantity || item.quantity || 1,
      unit_price: net === '' ? item.unit_price : net,
    };
    return applyDiscountOfferFields(updated, {
      baseGross: offerItem?.base_price,
      offerGross: offerItem?.offer_price ?? offer.effective_price,
      offerId: offer.offer_id,
      offerName: offer.name,
      offerCode: offer.sku,
      catalogSelectionId: offer.id,
      discountPercent: offer.discount_percent,
    });
  }

  if (offer.offer_kind === 'multi_product') {
    const firstItem = offer.items?.[0];
    const bundleGross = offer.bundle_offer_price ?? offer.effective_price;
    const bundleValue = parseFloat(bundleGross);
    const net = ventasPosPriceToUnitNet(bundleValue, {
      hasIgv: base.has_igv ?? true,
      igvRate,
    });
    let updated = {
      ...base,
      product_id: firstItem ? String(firstItem.product_id) : item.product_id,
      quantity: item.quantity || 1,
      unit_price: net === '' ? item.unit_price : net,
    };
    return applyMultiProductOfferFields(updated, offer, bundleGross);
  }

  return item;
};

export const hasDiscountOfferLine = (item) => {
  if (item?.offer_kind !== 'discount') return false;
  const listGross = parseFloat(item?.list_unit_price_gross);
  const offerGross = parseFloat(item?.offer_unit_price_gross);
  return Number.isFinite(listGross) && Number.isFinite(offerGross) && offerGross < listGross;
};

export const applyDiscountOfferFields = (
  item,
  {
    baseGross,
    offerGross,
    offerId = null,
    offerName = null,
    offerCode = null,
    catalogSelectionId = null,
    discountPercent = null,
  },
) => {
  const listGross = parseFloat(baseGross);
  const offerGrossValue = parseFloat(offerGross);
  if (!Number.isFinite(listGross) || !Number.isFinite(offerGrossValue) || offerGrossValue >= listGross) {
    return clearDiscountOfferFields(item);
  }
  const resolvedOfferId = offerId ?? item.sales_offer_id ?? null;
  const resolvedPercent =
    discountPercent != null && Number.isFinite(parseFloat(discountPercent))
      ? parseFloat(discountPercent)
      : roundPrice((1 - offerGrossValue / listGross) * 100, 4);
  return {
    ...item,
    sales_offer_id: resolvedOfferId,
    offer_kind: 'discount',
    list_unit_price_gross: roundPrice(listGross),
    offer_unit_price_gross: roundPrice(offerGrossValue),
    discount_percent: resolvedPercent,
    catalog_selection_id:
      catalogSelectionId ?? (resolvedOfferId ? `offer-${resolvedOfferId}` : item.catalog_selection_id ?? null),
    offer_name: offerName ?? item.offer_name ?? null,
    offer_code: offerCode ?? item.offer_code ?? null,
  };
};
